import { useRef, useState, type FormEvent } from 'react';
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFieldErrors } from '@/components/useFieldErrors';
import type { AskFailure, DomainHelp } from './useDomainHelp';

const FIELDS = ['question'] as const;

export interface DnsHelpPanelProps {
  help: DomainHelp;
  providerName: string;
  questionsLeft: number | null;
  exhausted: boolean;
  askFailure?: AskFailure | null;
  aiUnavailable?: boolean;
  asking?: boolean;
  disabled?: boolean;
  onRewrite: () => void;
  onAsk: (question: string) => Promise<boolean>;
}

export function DnsHelpPanel({
  help,
  providerName,
  questionsLeft,
  exhausted,
  askFailure = null,
  aiUnavailable = false,
  asking,
  disabled,
  onRewrite,
  onAsk,
}: DnsHelpPanelProps) {
  const { t } = useTranslation();
  const questionRef = useRef<HTMLInputElement>(null);
  const [question, setQuestion] = useState('');
  const fieldErrors = useFieldErrors(FIELDS, { question: questionRef }, asking === true);
  const unavailable = Boolean(help.aiDisabledReason) || aiUnavailable;

  const handleAsk = async (event: FormEvent) => {
    event.preventDefault();
    if (exhausted || unavailable || asking) return;
    const trimmed = question.trim();
    if (!trimmed) {
      fieldErrors.fail({ question: t('validation.question') });
      return;
    }
    try {
      // Only a successful answer clears the field; a failed ask resolves false.
      if (await onAsk(trimmed.slice(0, 300))) setQuestion('');
    } catch {
      // The screen shows the error; the typed question stays.
    }
  };

  return (
    <Stack spacing={2}>
      {unavailable ? <Alert severity="info">{t('domain.help.unavailable')}</Alert> : null}
      {exhausted ? <Alert severity="info">{t('domain.help.exhausted')}</Alert> : null}
      {help.instructions ? (
        <Paper variant="outlined">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1.25,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="subtitle2" component="h3" sx={{ flex: 1, minWidth: 0 }}>
              {t('domain.help.title', { provider: providerName })}
            </Typography>
            {disabled ? null : (
              <Button size="small" onClick={onRewrite}>
                {t('domain.rewrite')}
              </Button>
            )}
          </Box>
          <Typography
            component="pre"
            sx={{
              m: 0,
              p: 2,
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              typography: 'body2',
              color: 'text.secondary',
            }}
          >
            {help.instructions}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', px: 2, pb: 1.5, color: 'text.secondary' }}>
            {t('domain.help.disclaimer')}
          </Typography>
        </Paper>
      ) : null}
      {(help.questions ?? []).map((item, index) => (
        <Paper key={`${item.question ?? ''}-${index}`} variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {item.question}
          </Typography>
          {item.answer ? (
            <Typography
              variant="body2"
              sx={{ mt: 0.5, whiteSpace: 'pre-wrap', color: 'text.secondary' }}
            >
              {item.answer}
            </Typography>
          ) : null}
        </Paper>
      ))}
      {!unavailable && !disabled ? (
        <Stack component="form" noValidate spacing={1} onSubmit={(e) => void handleAsk(e)}>
          {/* Multiline: Enter adds a line; the form only makes the button a real submit. */}
          <TextField
            label={t('domain.help.askLabel')}
            value={question}
            onChange={(e) => {
              setQuestion(e.target.value.slice(0, 300));
              fieldErrors.clear('question');
            }}
            inputRef={questionRef}
            error={fieldErrors.errors.question !== undefined}
            helperText={fieldErrors.errors.question}
            disabled={exhausted || asking}
            required
            multiline
            minRows={2}
          />
          {askFailure === 'noAnswer' ? (
            <Alert role="alert" severity="info">
              {t('domain.help.noAnswer')}
            </Alert>
          ) : null}
          {askFailure === 'busy' ? (
            <Alert role="alert" severity="warning">
              {t('domain.help.busy')}
            </Alert>
          ) : null}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {/* Disabled only when the allowance is used up, and the panel says so above. */}
            <Button type="submit" variant="outlined" loading={asking === true} disabled={exhausted}>
              {t('domain.help.ask')}
            </Button>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {questionsLeft == null
                ? t('statements.unavailable')
                : t('domain.help.questionsLeft', { count: questionsLeft })}
            </Typography>
          </Box>
        </Stack>
      ) : null}
    </Stack>
  );
}
